/**
 * Themed Silhouette motion vocabulary.
 *
 * Springs are medium-weight; we want letters to settle like printed dots on
 * paper, not bounce like rubber. Multiple staggers — letters fill in along
 * the silhouette's path order (~30ms each), so the SHAPE writes itself.
 *
 * Two named springs:
 *   - `letterSpring` — for individual letter fade-ins (mass 1.0)
 *   - `cardSpring`   — for surface entrances (mass 0.9, slightly snappier)
 *
 * Two named timings:
 *   - `LETTER_STAGGER_MS` — between consecutive letters on the silhouette
 *   - `WORD_TRAVEL_MS`    — how long the celebrate-and-travel beat takes
 */

import { easing, timings } from '@/juice';

export const letterSpring = {
  type: 'spring' as const,
  stiffness: 240,
  damping: 24,
  mass: 1.0,
} as const;

export const cardSpring = {
  type: 'spring' as const,
  stiffness: 260,
  damping: 22,
  mass: 0.9,
} as const;

export const shelfSpring = {
  type: 'spring' as const,
  stiffness: 220,
  damping: 23,
  mass: 0.95,
} as const;

export const hoverSpring = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 18,
  mass: 0.7,
} as const;

/**
 * Diagonal-stagger step between adjacent letters on the grid.
 *
 * The reveal cascades along (row + col) so cells on the same diagonal
 * enter together. With a 12×12 grid, (r+c) ranges 0..22, so total reveal
 * = 22 × LETTER_STAGGER_MS plus one spring settle. At 18ms per step that's
 * ~400ms of cascade plus ~200ms spring settle ≈ ~600ms total — inside the
 * 400–600ms reveal budget the coordinator asked for.
 */
export const LETTER_STAGGER_MS = 18;

/** Stagger between successive entries in the "words to find" sidebar. */
export const WORD_LIST_STAGGER_MS = 35;

/** Time (ms) a celebrating word's letters lift before travel begins. */
export const WORD_LIFT_MS = 220;

/** Time (ms) for the floating travel beat from grid → found list. */
export const WORD_TRAVEL_MS = 900;

/** Time (ms) for the silhouette pulse-on-complete (2-3 cycles). */
export const COMPLETE_PULSE_MS = 1800;

export { easing, timings };
